import type { TestRun } from "@/lib/qa-types";

const statusPresentation: Record<TestRun["status"], { label: string; tone: string }> = {
    QUEUED: { label: "排队中", tone: "busy" },
    RUNNING: { label: "执行中", tone: "busy" },
    PASSED: { label: "通过", tone: "good" },
    FAILED: { label: "有缺陷", tone: "bad" },
    CANCELLED: { label: "已取消", tone: "bad" },
};

export function DefectsView({ runs }: { runs: TestRun[] }) {
    const passed = runs.filter((run) => run.status === "PASSED").length;
    const defects = runs.filter((run) => run.status === "FAILED").length;
    const active = runs.filter((run) => run.status === "RUNNING" || run.status === "QUEUED").length;

    return (
        <section className="workspace-view" aria-labelledby="defects-title">
            <div className="view-heading">
                <div>
                    <p className="eyebrow">EXECUTION ARCHIVE</p>
                    <h1 id="defects-title">缺陷中心</h1>
                    <p>统一保存全部执行记录；通过记录用于追溯，失败记录标记为待处理缺陷。</p>
                </div>
            </div>

            <section className="metrics" aria-label="执行记录统计">
                <Metric label="全部执行" tone="cyan" value={runs.length} />
                <Metric label="执行通过" tone="green" value={passed} />
                <Metric label="待处理缺陷" tone="orange" value={defects} />
                <Metric label="执行中" tone="purple" value={active} />
            </section>

            <section className="card runs-card" aria-label="全部执行记录">
                <header className="card-head">
                    <div>
                        <p>完整历史</p>
                        <h2>所有执行记录</h2>
                    </div>
                    <span>{runs.length} 条</span>
                </header>
                {runs.length === 0 ? (
                    <div className="empty-state">
                        <div>◫</div>
                        <p>暂无执行记录</p>
                        <span>完成一次任务执行后，记录会自动归档到这里。</span>
                    </div>
                ) : (
                    <div className="run-table">
                        {runs.map((run) => {
                            const presentation = statusPresentation[run.status];
                            return (
                                <article className="run-row" key={run.run_id}>
                                    <div className="run-icon">▶</div>
                                    <div className="run-name">
                                        <strong>{run.requirement || "未填写执行需求"}</strong>
                                        <span>{run.run_id}</span>
                                    </div>
                                    <span className={`status-pill ${presentation.tone}`}>{presentation.label}</span>
                                    <span className="run-detail">
                                        {run.environment.toUpperCase()} · {run.case_ids.length} 条场景 · {run.agent_id}
                                    </span>
                                    {run.source_url ? (
                                        <a
                                            aria-label={`打开 ${run.run_id} 来源页面`}
                                            href={run.source_url}
                                            rel="noreferrer"
                                            target="_blank"
                                        >
                                            ↗
                                        </a>
                                    ) : (
                                        <span>—</span>
                                    )}
                                </article>
                            );
                        })}
                    </div>
                )}
            </section>
        </section>
    );
}

function Metric({ label, tone, value }: { label: string; tone: string; value: number }) {
    return (
        <article className={`metric ${tone}`}>
            <div className="metric-icon" />
            <p>{label}</p>
            <strong>{value}</strong>
            <span>已归档</span>
        </article>
    );
}
